import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type ShiftNotificationType = 'new' | 'approved' | 'declined';

export interface ShiftNotification {
  type: ShiftNotificationType;
  team?: string;
  isClosing: boolean;
}

const NOTIFICATION_ORDER: Record<ShiftNotificationType, number> = {
  new: 0,
  approved: 1,
  declined: 2,
};

const NOTIFICATION_DISPLAY_DURATION = 7000;
const DISAPPEARING_ANIMATION_DURATION = 500;

const compareByDisplayOrder = (
  firstNotification: ShiftNotification,
  secondNotification: ShiftNotification,
): number => {
  const firstNotificationPosition = NOTIFICATION_ORDER[firstNotification.type];
  const secondNotificationPosition = NOTIFICATION_ORDER[secondNotification.type];

  return firstNotificationPosition - secondNotificationPosition;
};

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  readonly shiftNotifications = signal<ShiftNotification[]>([]);
  readonly isShiftAlertSoundActive: Subject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly closeTimers = new Map<ShiftNotificationType, ReturnType<typeof setTimeout>>();
  private readonly removeTimers = new Map<ShiftNotificationType, ReturnType<typeof setTimeout>>();

  showNotification(type: ShiftNotificationType, team?: string): void {
    this.clearTimer(this.closeTimers, type);
    this.clearTimer(this.removeTimers, type);

    const notification: ShiftNotification = { type, team, isClosing: false };
    const notificationsWithoutCurrentType = this.shiftNotifications().filter(
      (currentNotification): boolean => currentNotification.type !== type,
    );
    const updatedNotifications = [...notificationsWithoutCurrentType, notification].sort(
      compareByDisplayOrder,
    );

    this.shiftNotifications.set(updatedNotifications);

    if (type === 'new') {
      this.isShiftAlertSoundActive.next(true);
    }

    const closeTimer = setTimeout(
      (): void => this.closeNotification(type),
      NOTIFICATION_DISPLAY_DURATION - DISAPPEARING_ANIMATION_DURATION,
    );
    this.closeTimers.set(type, closeTimer);
  }

  closeNotification(type: ShiftNotificationType): void {
    this.clearTimer(this.closeTimers, type);
    const notification = this.shiftNotifications().find(
      (currentNotification): boolean => currentNotification.type === type,
    );

    if (!notification || notification.isClosing) {
      return;
    }

    this.shiftNotifications.update((notifications): ShiftNotification[] =>
      notifications.map((currentNotification): ShiftNotification => {
        return currentNotification.type === type
          ? { ...currentNotification, isClosing: true }
          : currentNotification;
      }),
    );

    if (type === 'new') {
      this.isShiftAlertSoundActive.next(false);
    }

    const removeTimer = setTimeout((): void => {
      this.shiftNotifications.update((notifications): ShiftNotification[] =>
        notifications.filter((currentNotification): boolean => currentNotification.type !== type),
      );
      this.removeTimers.delete(type);
    }, DISAPPEARING_ANIMATION_DURATION);
    this.removeTimers.set(type, removeTimer);
  }

  private clearTimer(
    timers: Map<ShiftNotificationType, ReturnType<typeof setTimeout>>,
    type: ShiftNotificationType,
  ): void {
    const timer = timers.get(type);
    if (timer === undefined) {
      return;
    }

    clearTimeout(timer);
    timers.delete(type);
  }
}
